// =====================================================
// utils/exportarLigaPdf.ts
// Exportación de fixture de liga a PDF usando jsPDF 1.5.x
// Agrupa partidos por fecha con detalles y resultado
// =====================================================

import jsPDF from 'jspdf';
import 'jspdf-autotable';

interface DetallesPartido {
  fecha?: string;
  fechaHasta?: string;
  hora?: string;
  lugar?: string;
  observaciones?: string;
}

interface Equipo {
  id: number;
  nombre: string;
}

interface PartidoLiga {
  id: number;
  local: Equipo;
  visitante: Equipo;
  detalles?: DetallesPartido;
  resultado?: string;
  ganador?: Equipo | null;
  sets?: Array<{ gamesLocal: number | null; gamesVisitante: number | null }>;
}

interface FechaLiga {
  numero: number;
  partidos: PartidoLiga[];
}

interface Liga {
  fechas: FechaLiga[];
}

interface InfoLiga {
  nombre: string;
  categoria?: string;
  genero?: string;
  deporte?: string;
  institucion?: string;
  tablaPosiciones?: any[];
}

function formatearDetalles(detalles?: DetallesPartido): string {
  if (!detalles) return '';
  const partes: string[] = [];
  if (detalles.fecha) {
    let fechaStr = detalles.fecha;
    if (detalles.fechaHasta) fechaStr += ` al ${detalles.fechaHasta}`;
    partes.push(fechaStr);
  }
  if (detalles.hora) partes.push(detalles.hora);
  if (detalles.lugar) partes.push(detalles.lugar);
  return partes.join(' | ');
}

export function exportarLigaPdf(liga: Liga, info: InfoLiga): void {
  if (!liga || !liga.fechas || liga.fechas.length === 0) return;

  const doc = new jsPDF('portrait') as any;
  doc.setProperties({
    title: `Fixture - ${info.nombre}`,
  });

  // Encabezado
  const nombreInstitucion = info.institucion || '';
  if (nombreInstitucion) {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(nombreInstitucion, 14, 12);
  }

  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text(info.nombre || 'Liga', 14, 22);

  // Subtítulo con género/categoría
  const subtituloPartes: string[] = [];
  if (info.genero) subtituloPartes.push(info.genero);
  if (info.categoria) subtituloPartes.push(`Cat. ${info.categoria}`);
  if (info.deporte) subtituloPartes.push(info.deporte);

  let cursorY = 27;
  if (subtituloPartes.length > 0) {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(subtituloPartes.join(' · '), 14, cursorY);
    cursorY += 5;
  }

  doc.setDrawColor(0, 0, 0);
  doc.line(14, cursorY, 196, cursorY);
  cursorY += 6;

  // Iterar por cada fecha
  for (const fecha of liga.fechas) {
    // Verificar si hay espacio suficiente para al menos el header + 1 fila
    const espacioNecesario = 14 + fecha.partidos.length * 8;
    if (cursorY + 20 > 280) {
      doc.addPage();
      cursorY = 15;
    }

    // Título de fecha
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text(`Fecha ${fecha.numero}`, 14, cursorY);
    cursorY += 2;

    // Tabla con autoTable
    const columnHeaders = ['#', 'Local', 'VS', 'Visitante', 'Detalles', 'Resultado'];
    const rows: any[][] = [];

    for (const partido of fecha.partidos) {
      const localNombre = partido.local?.nombre || '-';
      const visitanteNombre = partido.visitante?.nombre || '-';
      const detalles = formatearDetalles(partido.detalles);
      const resultado = partido.resultado || '';

      rows.push([
        `P${partido.id}`,
        localNombre,
        'vs',
        visitanteNombre,
        detalles,
        resultado,
      ]);
    }

    (doc as any).autoTable(columnHeaders, rows, {
      startY: cursorY,
      margin: { left: 14, right: 14 },
      styles: {
        fontSize: 8,
        cellPadding: 2,
      },
      headStyles: {
        fillColor: [196, 197, 197],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
      },
      columnStyles: {
        0: { cellWidth: 12 },   // #
        1: { cellWidth: 38 },   // Local
        2: { cellWidth: 10, halign: 'center' },   // VS
        3: { cellWidth: 38 },   // Visitante
        4: { cellWidth: 50 },   // Detalles
        5: { cellWidth: 34 },   // Resultado
      },
      didParseCell: function (data: any) {
        // Resaltar resultado si existe
        if (data.column.index === 5 && data.section === 'body') {
          if (data.cell.raw && data.cell.raw.trim() !== '') {
            data.cell.styles.fontStyle = 'bold';
          }
        }
      },
    });

    cursorY = (doc as any).lastAutoTable.finalY + 8;
  }

  // ── SECCIÓN 2: Partidos en orden cronológico ──────────────
  // Recopilar todos los partidos con fecha/hora y ordenarlos
  interface PartidoCrono {
    fechaNum: number; // numero de fecha/ronda
    local: string;
    visitante: string;
    detalleFecha: string;
    hora: string;
    lugar: string;
    resultado: string;
  }

  const partidosCrono: PartidoCrono[] = [];

  for (const fecha of liga.fechas) {
    for (const partido of fecha.partidos) {
      const detFecha = partido.detalles?.fecha || '';
      const detHora = partido.detalles?.hora || '';
      const detLugar = partido.detalles?.lugar || '';

      partidosCrono.push({
        fechaNum: fecha.numero,
        local: partido.local?.nombre || '-',
        visitante: partido.visitante?.nombre || '-',
        detalleFecha: detFecha,
        hora: detHora,
        lugar: detLugar,
        resultado: partido.resultado || '',
      });
    }
  }

  // Normalizar fecha a YYYY-MM-DD para ordenar correctamente
  function normalizarFechaParaSort(fecha: string): string {
    if (!fecha) return '9999-12-31';
    // Si ya es YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}$/.test(fecha)) return fecha;
    // Si es DD/MM/YYYY
    const match = fecha.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (match) return `${match[3]}-${match[2]}-${match[1]}`;
    // Intentar parsear como Date
    const d = new Date(fecha);
    if (!isNaN(d.getTime())) {
      return d.toISOString().substring(0, 10);
    }
    return '9999-12-31';
  }

  // Ordenar por fecha y hora (más viejo primero, sin fecha al final)
  partidosCrono.sort((a, b) => {
    const fechaA = normalizarFechaParaSort(a.detalleFecha);
    const fechaB = normalizarFechaParaSort(b.detalleFecha);

    if (fechaA !== fechaB) return fechaA.localeCompare(fechaB);

    // Misma fecha: comparar por hora
    const horaA = a.hora || '99:99';
    const horaB = b.hora || '99:99';
    return horaA.localeCompare(horaB);
  });

  // Solo generar la sección si hay al menos un partido con fecha
  const tieneAlgunaFecha = partidosCrono.some(p => p.detalleFecha);

  if (tieneAlgunaFecha) {
    // Nueva página para la sección cronológica
    doc.addPage();

    // Encabezado
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('Fixture Cronológico', 14, 15);

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(`${info.nombre} — Partidos ordenados por fecha y hora`, 14, 22);
    doc.line(14, 25, 196, 25);

    const columnHeadersCrono = ['Fecha', 'Hora', 'Local', 'VS', 'Visitante', 'Lugar', 'Resultado'];
    const rowsCrono: any[][] = [];

    for (const p of partidosCrono) {
      rowsCrono.push([
        p.detalleFecha || '—',
        p.hora || '—',
        p.local,
        'vs',
        p.visitante,
        p.lugar || '',
        p.resultado,
      ]);
    }

    (doc as any).autoTable(columnHeadersCrono, rowsCrono, {
      startY: 28,
      margin: { left: 14, right: 14 },
      styles: {
        fontSize: 7,
        cellPadding: 2,
      },
      headStyles: {
        fillColor: [196, 197, 197],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
      },
      columnStyles: {
        0: { cellWidth: 22 },   // Fecha
        1: { cellWidth: 14 },   // Hora
        2: { cellWidth: 36 },   // Local
        3: { cellWidth: 8, halign: 'center' },   // VS
        4: { cellWidth: 36 },   // Visitante
        5: { cellWidth: 30 },   // Lugar
        6: { cellWidth: 30 },   // Resultado
      },
      didParseCell: function (data: any) {
        if (data.column.index === 6 && data.section === 'body') {
          if (data.cell.raw && data.cell.raw.trim() !== '') {
            data.cell.styles.fontStyle = 'bold';
          }
        }
      },
    });
  }

  // ── SECCIÓN 3: Tabla de Posiciones ──────────────────────────
  if (info.tablaPosiciones && info.tablaPosiciones.length > 0) {
    doc.addPage();

    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('Tabla de Posiciones', 14, 15);

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(info.nombre || 'Liga', 14, 22);
    doc.line(14, 25, 196, 25);

    // Detectar deporte para ajustar columnas
    const esFutbol = info.deporte === 'Futbol';

    const columnHeadersTabla = esFutbol
      ? ['#', 'Equipo', 'PJ', 'PG', 'PE', 'PP', 'GF', 'GC', 'DG', 'Pts']
      : ['#', 'Equipo', 'PJ', 'PG', 'PP', 'SG', 'SP', 'DS', 'GG', 'GP', 'DG', 'Pts'];

    const rowsTabla: any[][] = [];

    info.tablaPosiciones.forEach((stats: any, idx: number) => {
      const nombre = stats.equipo?.nombre || '-';

      if (esFutbol) {
        rowsTabla.push([
          idx + 1,
          nombre,
          stats.pj,
          stats.pg,
          stats.pe,
          stats.pp,
          stats.gamesGanados,
          stats.gamesPerdidos,
          stats.gamesDiff,
          stats.puntos,
        ]);
      } else {
        rowsTabla.push([
          idx + 1,
          nombre,
          stats.pj,
          stats.pg,
          stats.pp,
          stats.setsGanados,
          stats.setsPerdidos,
          stats.setsDiff,
          stats.gamesGanados,
          stats.gamesPerdidos,
          stats.gamesDiff,
          stats.puntos,
        ]);
      }
    });

    const columnStylesTabla: any = esFutbol
      ? {
          0: { cellWidth: 8, halign: 'center' },
          1: { cellWidth: 45 },
          2: { cellWidth: 12, halign: 'center' },
          3: { cellWidth: 12, halign: 'center' },
          4: { cellWidth: 12, halign: 'center' },
          5: { cellWidth: 12, halign: 'center' },
          6: { cellWidth: 12, halign: 'center' },
          7: { cellWidth: 12, halign: 'center' },
          8: { cellWidth: 12, halign: 'center' },
          9: { cellWidth: 14, halign: 'center' },
        }
      : {
          0: { cellWidth: 8, halign: 'center' },
          1: { cellWidth: 35 },
          2: { cellWidth: 11, halign: 'center' },
          3: { cellWidth: 11, halign: 'center' },
          4: { cellWidth: 11, halign: 'center' },
          5: { cellWidth: 11, halign: 'center' },
          6: { cellWidth: 11, halign: 'center' },
          7: { cellWidth: 11, halign: 'center' },
          8: { cellWidth: 11, halign: 'center' },
          9: { cellWidth: 11, halign: 'center' },
          10: { cellWidth: 11, halign: 'center' },
          11: { cellWidth: 14, halign: 'center' },
        };

    (doc as any).autoTable(columnHeadersTabla, rowsTabla, {
      startY: 28,
      margin: { left: 14, right: 14 },
      styles: {
        fontSize: 8,
        cellPadding: 2,
      },
      headStyles: {
        fillColor: [196, 197, 197],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        halign: 'center',
      },
      columnStyles: columnStylesTabla,
      didParseCell: function (data: any) {
        // Resaltar columna de puntos
        const lastCol = esFutbol ? 9 : 11;
        if (data.column.index === lastCol && data.section === 'body') {
          data.cell.styles.fontStyle = 'bold';
        }
        // Primeras 3 posiciones en negrita
        if (data.section === 'body' && data.row.index < 3) {
          data.cell.styles.fontStyle = 'bold';
        }
      },
    });
  }

  // Abrir en nueva pestaña
  window.open(doc.output('bloburl'), '_blank');
}
